/**
 * I gesti del banco di ricezione.
 *
 * Sta con la schermata che li usa, come i `*-api.ts` dell'ufficio: erano gli
 * unici due modi diversi di fare la stessa cosa nelle due app.
 *
 * Il trasporto — come si compone una richiesta, come si leggono i tre modi di
 * dire di no — sta in `@errebi/sessione`, che lo condivide con l'ufficio. Qui
 * resta l'elenco dei gesti, che è cosa di questo reparto e di nessun altro.
 */

import { chiama, comando, leggi } from '@errebi/sessione';
import { zEsitoFoto, zSchedeAcquisto, type EsitoComando, type EsitoFoto } from '@errebi/shared';

export {
  accedi,
  confermaIdentita,
  ConfermaRichiesta,
  ErroreApi,
  quandoScadeLaSessione,
  segnalaScadenza,
  SessioneScaduta,
  type Identita,
} from '@errebi/sessione';

/* ── il banco ─────────────────────────────────────────────────────────── */

export function creaBanco(postazione: string, token: string) {
  const base = `/ricezione/${encodeURIComponent(postazione)}`;
  const post = (p: string, corpo?: unknown): Promise<EsitoComando> =>
    comando(`${base}${p}`, token, corpo);

  return {
    vista: () => chiama(`${base}/vista`, token),
    /**
     * Le bolle che il banco può aprire. È una lettura, non un gesto: non passa
     * da `post` e non torna un `EsitoComando`.
     *
     * La ricerca per DDT, fornitore e riferimento si fa **su questa lista**,
     * nella schermata: `manage_purchase_orders/find` cerca su cinque campi e
     * nessuno dei tre è fra quelli. Il numero digitato per intero va invece
     * direttamente ad `apriArrivo`, ed è l'unica strada verso la bolla di un
     * arrivo troppo vecchio per stare nella finestra.
     */
    attesi: () => leggi(`${base}/attesi`, token, zSchedeAcquisto),
    /** Si dichiara l'arrivo, invece di lasciarlo dedurre dal capo scansionato. */
    apriArrivo: (numero: string) => post('/arrivo', { numero }),
    /** Si smette di ricevere quella bolla. Nessun argomento: l'arrivo lo sa il banco. */
    staccaArrivo: () => post('/arrivo/stacca'),
    /** Si butta via il contato su quell'arrivo: le sue cassette tornano libere. */
    annullaArrivo: () => post('/arrivo/annulla'),
    scansionaCapo: (sku: string, taglia: string) => post('/capo/scansione', { sku, taglia }),
    agganciaCapo: (ordine: string, riga: string) => post('/capo', { ordine, riga }),
    apriCassetta: (cassetta: string) => post('/cassetta', { cassetta }),
    /** Without `quantita` it is the tap: one garment. */
    contaCapo: (taglia: string, quantita?: number) =>
      post('/conta', quantita === undefined ? { taglia } : { taglia, quantita }),
    togliCapo: (taglia: string) => post('/togli', { taglia }),
    /** La foto è facoltativa: senza webcam il fallato si dichiara lo stesso. */
    segnaFallato: (taglia: string, quantita: number, foto?: string) =>
      post('/fallato', { taglia, quantita, ...(foto === undefined ? {} : { foto }) }),
    /**
     * Lo scatto al banco (regola 38-bis): l'unico gesto multipart.
     *
     * La `FormData` si compone **qui dentro**, a ogni chiamata, e non si accetta
     * già pronta da chi chiama. La ragione è il ritentativo: un gesto attribuito
     * torna 403, la sessione apre la richiesta della password e **richiama
     * questa funzione**. Una `FormData` è rispedibile, ma «si ricostruisce a
     * ogni tentativo» costa nulla ed è una regola che non si può infrangere per
     * distrazione.
     *
     * Non dice di quale riga sia la foto: il soggetto è il capo agganciato, che
     * il banco già conosce. Vedi `zScattaFoto`.
     */
    scattaFoto: (immagine: Blob, taglia?: string): Promise<EsitoFoto> => {
      const modulo = new FormData();
      modulo.append('foto', immagine, 'scatto.jpg');
      if (taglia !== undefined) modulo.append('taglia', taglia);
      return leggi(`${base}/foto`, token, zEsitoFoto, { metodo: 'POST', corpo: modulo });
    },
    svuota: (cassetta: string) => post(`/cassetta/${cassetta}/svuota`),
    stampaEtichette: (cassetta: string) => post(`/cassetta/${cassetta}/etichette`),
    ristampaEtichette: (cassetta: string) => post(`/cassetta/${cassetta}/etichette/ristampa`),
    conferma: (cassetta: string) => post(`/cassetta/${cassetta}/conferma`),
    marcaPronte: (cassette: string[]) => post('/pronte', { cassette }),
  };
}

export type Banco = ReturnType<typeof creaBanco>;
